/**
 * @author Nihar<nihar.baishya@314ecorp.com>
 * @description utils for query builder
 */

import _ from 'lodash';
import { JsonCaseGroup, JsonGroup, JsonRule, JsonTree, Utils, ValueSource } from '@react-awesome-query-builder/antd';

interface IQueryRuleParams {
	field: string;
	operator: string;
	value: AnyType;
	valueSrc?: ValueSource[];
	valueType?: string[];
}

// function to prepare query rule
const prepareQueryRule = ({ field, operator, value, valueType, valueSrc }: IQueryRuleParams): JsonRule => {
	return {
		type: 'rule',
		id: Utils.uuid(),
		properties: {
			fieldSrc: 'field',
			field,
			operator,
			value,
			valueSrc: valueSrc ?? ['value'],
			valueType: valueType ?? ['text'],
		},
	};
};

const prepareQueryGroupRule = ({
	children,
	conjunction = 'OR',
	not = false,
}: {
	children: JsonGroup[] | JsonRule[];
	conjunction?: 'OR' | 'AND';
	not?: boolean;
}): JsonGroup => {
	return {
		type: 'group',
		id: Utils.uuid(),
		properties: { conjunction, not },
		children1: children,
	};
};

// function to prepare query group
const prepareQueryGroup = ({ children, conjunction = 'AND' }: { children: JsonRule[]; conjunction?: 'AND' | 'OR' }): JsonTree => {
	return prepareQueryGroupRule({ children: [prepareQueryGroupRule({ children, conjunction, not: false })] });
};

// function to insert rule into group
const insertRuleIntoGroup = (group: JsonTree, rule: JsonRule): JsonTree => {
	group.children1 = _.map(group.children1 as JsonCaseGroup[], (child) => {
		return {
			...child,
			children1: [...(child?.children1 || []), rule],
		};
	});
	return group;
};

// function to remove rule from group
const removeRuleFromGroup = (group: JsonTree, fieldName: string): JsonTree => {
	if (group && group.children1 && Array.isArray(group.children1)) {
		group.children1 = _.map(group.children1 as JsonCaseGroup[], (child) => {
			if (child.children1 && Array.isArray(child.children1)) {
				return {
					...child,
					children1: _.filter(child.children1, (rule) => {
						return !(rule.type === 'rule' && rule.properties?.field === fieldName);
					}),
				};
			}
			return child;
		});
	}
	return group;
};

// function to add rule into search query, if no group exists, create a new group
const addRuleToQuery = (criteria: JsonTree, rule: JsonRule): JsonTree => {
	const clonedCriteria = _.cloneDeep(criteria);
	if (_.isEmpty(clonedCriteria)) {
		return prepareQueryGroup({ children: [rule] });
	} else {
		return insertRuleIntoGroup(clonedCriteria, rule);
	}
};

export { prepareQueryRule, prepareQueryGroup, insertRuleIntoGroup, prepareQueryGroupRule, removeRuleFromGroup, addRuleToQuery };
